import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users,
  Crown,
  Shield,
  DollarSign,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Building2,
  Info,
  FileText,
  Gavel,
  Vote,
  Truck,
  AlertTriangle,
  Scale,
  FolderOpen,
  ShoppingCart,
  HardHat,
  PartyPopper,
} from 'lucide-react';
import CollapsibleSection from './CollapsibleSection';

interface TeamMember {
  role: string;
  color: 'green' | 'purple' | 'blue' | 'teal' | 'orange';
  subordinates?: TeamMember[]; // Optional nested hierarchy
}

interface Team {
  id: string;
  name: string;
  description: string;
  structure: TeamMember[]; // Hierarchical structure with nesting
}

interface Department {
  name: string;
  icon: any;
  color: string;
  description: string;
  directorCount: number;
  teams: Team[];
}

const departments: Department[] = [
  {
    name: 'Finance',
    icon: DollarSign,
    color: 'green',
    description: 'Budget oversight and financial planning',
    directorCount: 2,
    teams: [
      {
        id: 'finance-1',
        name: 'Budget & Planning',
        description: 'Annual budgets and forecasting',
        structure: [
          {
            role: 'Budget Director',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Analyst',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'finance-2',
        name: 'Revenue & Collections',
        description: 'Fee collection and revenue management',
        structure: [
          {
            role: 'Operations Manager',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Coordinator',
                color: 'teal',
                subordinates: [{ role: 'Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Governance & Voting',
    icon: Vote,
    color: 'purple',
    description: 'Elections, proposals, and voting processes',
    directorCount: 2,
    teams: [
      {
        id: 'gov-1',
        name: 'Elections Administration',
        description: 'Managing all community elections',
        structure: [
          {
            role: 'Elections Director',
            color: 'green',
            subordinates: [
              {
                role: 'Poll Coordinator',
                color: 'teal',
                subordinates: [
                  { role: 'Poll Volunteer 1', color: 'orange' },
                  { role: 'Poll Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'gov-2',
        name: 'Proposal Review',
        description: 'Proposal vetting and ballot preparation',
        structure: [
          {
            role: 'Review Director',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Reviewer',
                color: 'teal',
                subordinates: [{ role: 'Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Dispute Resolution',
    icon: Gavel,
    color: 'orange',
    description: 'Fair conflict resolution and community decisions',
    directorCount: 1,
    teams: [
      {
        id: 'dispute-1',
        name: 'Mediation Services',
        description: 'First-line conflict resolution',
        structure: [
          {
            role: 'Lead Mediator',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Mediator',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Community Property',
    icon: Building2,
    color: 'emerald',
    description: 'Community facilities and property management',
    directorCount: 2,
    teams: [
      {
        id: 'property-1',
        name: 'Facilities Management',
        description: 'Building maintenance and operations',
        structure: [
          {
            role: 'Facilities Manager',
            color: 'green',
            subordinates: [
              {
                role: 'Maintenance Lead',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'property-2',
        name: 'Parks & Recreation',
        description: 'Green spaces and recreational areas',
        structure: [
          {
            role: 'Parks Manager',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Coordinator',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Emergency Management',
    icon: AlertTriangle,
    color: 'red',
    description: 'Emergency response and coordination',
    directorCount: 1,
    teams: [
      {
        id: 'emergency-1',
        name: 'Crisis Response',
        description: 'Immediate emergency coordination',
        structure: [
          {
            role: 'Emergency Director',
            color: 'green',
            subordinates: [
              {
                role: 'Response Lead',
                color: 'teal',
                subordinates: [
                  { role: 'Emergency Volunteer 1', color: 'orange' },
                  { role: 'Emergency Volunteer 2', color: 'orange' },
                ],
              },
              {
                role: 'Communications Lead',
                color: 'purple',
                subordinates: [{ role: 'Comm Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Legal & Compliance',
    icon: Scale,
    color: 'slate',
    description: 'Legal matters and governance compliance',
    directorCount: 2,
    teams: [
      {
        id: 'legal-1',
        name: 'Legal Affairs',
        description: 'Contract review and legal counsel',
        structure: [
          {
            role: 'Legal Counsel',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Paralegal',
                color: 'teal',
                subordinates: [{ role: 'Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
      {
        id: 'legal-2',
        name: 'Compliance Monitoring',
        description: 'Regulatory compliance oversight',
        structure: [
          {
            role: 'Compliance Officer',
            color: 'green',
            subordinates: [
              {
                role: 'Records Coordinator',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Group Purchasing & Commerce',
    icon: ShoppingCart,
    color: 'blue',
    description: 'Bulk buying and community commerce',
    directorCount: 3,
    teams: [
      {
        id: 'purchase-1',
        name: 'Vendor Relations',
        description: 'Managing supplier partnerships',
        structure: [
          { role: 'Vendor Manager', color: 'green' },
          { role: 'Contract Specialist', color: 'purple' },
        ],
      },
      {
        id: 'purchase-2',
        name: 'Group Buy Coordination',
        description: 'Organizing community bulk purchases',
        structure: [
          {
            role: 'Logistics Manager',
            color: 'green',
            subordinates: [
              { role: 'Volunteer Coord 1', color: 'teal' },
              { role: 'Volunteer Coord 2', color: 'teal' },
            ],
          },
        ],
      },
      {
        id: 'purchase-3',
        name: 'Quality Assurance',
        description: 'Product vetting and quality control',
        structure: [{ role: 'QA Lead', color: 'green' }],
      },
    ],
  },
  {
    name: 'Infrastructure & Public Works',
    icon: HardHat,
    color: 'amber',
    description: 'Roads, utilities, and maintenance',
    directorCount: 3,
    teams: [
      {
        id: 'infra-1',
        name: 'Roads & Transportation',
        description: 'Street maintenance and traffic management',
        structure: [
          {
            role: 'Roads Supervisor',
            color: 'green',
            subordinates: [
              {
                role: 'Maintenance Lead',
                color: 'teal',
                subordinates: [
                  { role: 'Road Volunteer 1', color: 'orange' },
                  { role: 'Road Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'infra-2',
        name: 'Utilities Coordination',
        description: 'Water, power, and connectivity liaison',
        structure: [
          {
            role: 'Utilities Manager',
            color: 'green',
            subordinates: [
              {
                role: 'Project Coordinator',
                color: 'teal',
                subordinates: [{ role: 'Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
      {
        id: 'infra-3',
        name: 'Maintenance Operations',
        description: 'Repair and upkeep services',
        structure: [
          {
            role: 'Operations Manager',
            color: 'green',
            subordinates: [
              {
                role: 'Volunteer Lead 1',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 1', color: 'orange' },
                  { role: 'Volunteer 2', color: 'orange' },
                ],
              },
              {
                role: 'Volunteer Lead 2',
                color: 'teal',
                subordinates: [
                  { role: 'Volunteer 3', color: 'orange' },
                  { role: 'Volunteer 4', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Community Engagement & Programs',
    icon: PartyPopper,
    color: 'pink',
    description: 'Events, education, and social programs',
    directorCount: 2,
    teams: [
      {
        id: 'engage-1',
        name: 'Community Events',
        description: 'Organizing festivals and gatherings',
        structure: [
          {
            role: 'Events Director',
            color: 'green',
            subordinates: [
              {
                role: 'Event Coordinator',
                color: 'teal',
                subordinates: [
                  { role: 'Event Volunteer 1', color: 'orange' },
                  { role: 'Event Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'engage-2',
        name: 'Education & Outreach',
        description: 'Workshops and information campaigns',
        structure: [
          {
            role: 'Outreach Director',
            color: 'green',
            subordinates: [
              {
                role: 'Workshop Coordinator',
                color: 'teal',
                subordinates: [{ role: 'Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
      {
        id: 'engage-3',
        name: 'Social Services',
        description: 'Health, safety, and wellness programs',
        structure: [
          {
            role: 'Program Director',
            color: 'green',
            subordinates: [
              {
                role: 'Health Coordinator',
                color: 'teal',
                subordinates: [{ role: 'Health Volunteer 1', color: 'orange' }],
              },
              {
                role: 'Wellness Coordinator',
                color: 'teal',
                subordinates: [{ role: 'Wellness Volunteer 1', color: 'orange' }],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Transition & Succession',
    icon: Users,
    color: 'cyan',
    description: 'Emergency succession and governance continuity',
    directorCount: 3,
    teams: [
      {
        id: 'transition-1',
        name: 'Succession Planning',
        description: 'Emergency replacement and continuity oversight',
        structure: [
          {
            role: 'Transition Coordinator',
            color: 'green',
            subordinates: [
              {
                role: 'Observer Lead',
                color: 'teal',
                subordinates: [
                  { role: 'Observer Volunteer 1', color: 'orange' },
                  { role: 'Observer Volunteer 2', color: 'orange' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export function HierarchicalGovernanceChart({ cityName }: { cityName: string }) {
  const [expandedDepartments, setExpandedDepartments] = useState<string[]>([]);
  const [expandedTeams, setExpandedTeams] = useState<string[]>([]);

  const toggleDepartment = (deptName: string) => {
    setExpandedDepartments((prev) =>
      prev.includes(deptName) ? prev.filter((d) => d !== deptName) : [...prev, deptName],
    );
  };

  const toggleTeam = (teamId: string) => {
    setExpandedTeams((prev) =>
      prev.includes(teamId) ? prev.filter((t) => t !== teamId) : [...prev, teamId],
    );
  };

  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    teal: 'bg-teal-50 border-teal-200 text-teal-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    pink: 'bg-pink-50 border-pink-200 text-pink-800',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
  };

  return (
    <CollapsibleSection
      title={
        <>
          <Users className="w-5 h-5" />
          Your Leadership Structure, Organized
        </>
      }
      subtitle={
        <span className="text-sm text-muted-foreground">
          How {cityName} roles fit into a clear chain of command
        </span>
      }
      className=""
    >
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-3 mx-2 sm:mx-6">
        <Info className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
        <div className="space-y-2 text-sm text-blue-800">
          <p>
            <strong>Reality Check:</strong> Most city-level resident communities don't have this
            structure yet. Unlike city government (mayor, council, departments), there's typically
            no organized framework for <em>residents</em> to collectively govern themselves. No
            committees, no clear decision-making process, no accountability structure.
          </p>
          <p>
            <strong>That's what you're building.</strong> This isn't about "organizing existing
            committees." It's about creating the governance capacity that never existed. The
            framework above shows what you'll build together using our platform: a clear chain of
            command where infrastructure maintenance, budget decisions, and compliance don't stall.
            Every decision is recorded, every responsibility is tracked digitally, and nothing falls
            through the cracks.
          </p>
        </div>
      </div>
      <div className="w-full overflow-hidden">
        <div className="space-y-8 py-4 min-w-0 px-2 sm:px-0 max-w-full">
          {/* Community Level */}
          <div className="flex flex-col items-center space-y-6 max-w-full">
            {/* Community Elect */}
            <div className="flex flex-col items-center space-y-4">
              <div className="w-40 sm:w-48 h-20 sm:h-24 bg-gradient-to-br from-yellow-300 to-yellow-500 border-2 border-yellow-600 rounded-xl flex flex-col items-center justify-center shadow-lg">
                <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-900 mb-1" />
                <div className="text-base sm:text-lg font-bold text-yellow-900">
                  Community Elect
                </div>
                <div className="text-xs text-yellow-800">Override Authority</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-w-xs text-center">
                <div className="text-xs font-semibold text-gray-700 mb-1">Legacy Role:</div>
                <div className="text-sm text-gray-800 font-medium">President/Chair</div>
                <div className="text-xs italic text-gray-600">"I make final calls"</div>
              </div>
            </div>

            {/* Connection line down */}
            <div className="w-px h-8 bg-gray-400"></div>

            {/* Community Directors */}
            <div className="space-y-4 max-w-full">
              <div className="text-center">
                <div className="text-sm font-semibold text-gray-700 mb-2">Community Directors</div>
                {/* Connection lines - responsive width, constrained to viewport */}
                <div className="w-40 xs:w-56 sm:w-72 md:w-96 h-px bg-gray-400 mx-auto"></div>
                <div className="relative">
                  <div className="absolute -top-px left-1/3 w-px h-6 sm:h-8 bg-gray-400"></div>
                  <div className="absolute -top-px left-1/2 w-px h-6 sm:h-8 bg-gray-400"></div>
                  <div className="absolute -top-px left-2/3 w-px h-6 sm:h-8 bg-gray-400"></div>
                </div>
              </div>

              <div className="flex justify-center flex-wrap gap-1 xs:gap-2 sm:gap-4 lg:gap-8 max-w-full">
                {/* Director A */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-gradient-to-br from-green-300 to-green-500 border-2 border-green-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-green-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-green-900">Director A</div>
                    <div className="text-xs text-green-800 hidden xs:block">Operations</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">VP, PM</div>
                  </div>
                </div>

                {/* Director B */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-gradient-to-br from-purple-300 to-purple-500 border-2 border-purple-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-purple-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-purple-900">Director B</div>
                    <div className="text-xs text-purple-800 hidden xs:block">Resources</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">Treas, Sec</div>
                  </div>
                </div>

                {/* Director C */}
                <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                  <div className="w-20 xs:w-24 sm:w-32 h-12 xs:h-14 sm:h-18 bg-gradient-to-br from-blue-300 to-blue-500 border-2 border-blue-600 rounded-lg flex flex-col items-center justify-center shadow-md">
                    <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-blue-900 mb-0.5 sm:mb-1" />
                    <div className="text-xs sm:text-sm font-bold text-blue-900">Director C</div>
                    <div className="text-xs text-blue-800 hidden xs:block">Community</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded p-1 sm:p-2 text-xs text-center w-20 xs:w-24 sm:max-w-28">
                    <div className="font-medium text-gray-700 hidden sm:block">Legacy:</div>
                    <div className="text-gray-600 text-xs">Chair, Mod</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Connection line to departments */}
            <div className="w-px h-8 bg-gray-400"></div>

            {/* Departments Section */}
            <div className="w-full">
              <div className="text-center mb-4">
                <div className="text-sm font-semibold text-gray-700 mb-2">
                  {cityName} Departments
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="text-sm font-medium text-blue-800 mb-1">
                    ✓ Volunteer-Powered Community Governance
                  </div>
                  <div className="text-sm text-blue-700">
                    Every department is built on community volunteers. Without volunteer
                    participation, governance doesn't exist. Click any department to see how
                    volunteers form the backbone of each team's operations, from emergency response
                    to event coordination.
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Expand departments to see volunteer participation pathways
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((dept) => {
                  const isExpanded = expandedDepartments.includes(dept.name);

                  return (
                    <div
                      key={dept.name}
                      className={`border rounded-lg p-4 ${colorClasses[dept.color as keyof typeof colorClasses]}`}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDepartment(dept.name)}
                        className="w-full justify-between p-2 h-auto hover:bg-gradient-to-r hover:from-blue-500/90 hover:to-blue-600/90 hover:text-white rounded-md whitespace-normal"
                      >
                        <div className="flex items-start space-x-2 min-w-0 flex-1">
                          <dept.icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div className="text-left min-w-0 flex-1">
                            <div className="font-semibold text-sm break-words">{dept.name}</div>
                            <div className="text-xs opacity-75 break-words leading-tight">
                              {dept.description}
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      </Button>

                      {isExpanded && (
                        <div className="mt-4 space-y-4 border-t pt-3">
                          {/* Department SubPanel */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-center gap-2 text-xs font-semibold">
                              <span>Department SubPanel</span>
                              <span className="text-gray-500">
                                ({dept.directorCount} Director{dept.directorCount > 1 ? 's' : ''})
                              </span>
                            </div>

                            {/* Department Elect */}
                            <div className="flex justify-center">
                              <div className="w-24 h-12 bg-gradient-to-br from-yellow-200 to-yellow-400 border border-yellow-500 rounded-lg flex flex-col items-center justify-center">
                                <Crown className="w-3 h-3 text-yellow-800" />
                                <div className="text-xs font-bold text-yellow-800">
                                  Department Elect
                                </div>
                              </div>
                            </div>

                            {/* Department Directors - Variable Count */}
                            <div className="flex justify-center space-x-2">
                              {Array.from({ length: dept.directorCount }, (_, i) => (
                                <div
                                  key={i}
                                  className={`w-20 h-10 border rounded flex items-center justify-center ${
                                    i === 0
                                      ? 'bg-gradient-to-br from-green-200 to-green-400 border-green-500'
                                      : i === 1
                                        ? 'bg-gradient-to-br from-purple-200 to-purple-400 border-purple-500'
                                        : 'bg-gradient-to-br from-blue-200 to-blue-400 border-blue-500'
                                  }`}
                                >
                                  <div
                                    className={`text-xs font-bold ${
                                      i === 0
                                        ? 'text-green-800'
                                        : i === 1
                                          ? 'text-purple-800'
                                          : 'text-blue-800'
                                    }`}
                                  >
                                    Director {String.fromCharCode(65 + i)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Teams Section */}
                          {dept.teams.length > 0 && (
                            <div className="space-y-3 border-t pt-3">
                              <div className="flex items-center justify-center gap-2 text-xs font-semibold text-gray-700 flex-wrap">
                                <Users className="w-3 h-3" />
                                <span className="whitespace-nowrap">Teams in {dept.name}</span>
                                <Badge variant="outline" className="text-xs whitespace-nowrap">
                                  {dept.teams.length} Team{dept.teams.length > 1 ? 's' : ''}
                                </Badge>
                              </div>

                              {/* Teams Grid */}
                              <div className="grid grid-cols-1 gap-2">
                                {dept.teams.map((team) => {
                                  const isTeamExpanded = expandedTeams.includes(team.id);

                                  return (
                                    <div
                                      key={team.id}
                                      className="border border-gray-300 rounded-lg p-2 bg-white/50"
                                    >
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleTeam(team.id)}
                                        className="w-full justify-between p-2 h-auto hover:bg-gradient-to-r hover:from-blue-500/90 hover:to-blue-600/90 rounded-md group"
                                      >
                                        <div className="flex items-start space-x-2 min-w-0 flex-1">
                                          <Shield className="w-3 h-3 flex-shrink-0 mt-0.5 text-gray-600 group-hover:text-white transition-colors" />
                                          <div className="text-left min-w-0 flex-1">
                                            <div className="font-medium text-xs text-gray-800 break-words group-hover:text-white transition-colors">
                                              {team.name}
                                            </div>
                                            <div className="text-xs text-gray-600 break-words leading-tight group-hover:text-white/90 transition-colors">
                                              {team.description}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-2">
                                          {isTeamExpanded ? (
                                            <ChevronDown className="w-3 h-3 text-gray-600 group-hover:text-white transition-colors" />
                                          ) : (
                                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-white transition-colors" />
                                          )}
                                        </div>
                                      </Button>

                                      {isTeamExpanded && (
                                        <div className="mt-2 space-y-3 border-t pt-2">
                                          <div className="text-xs text-center font-semibold text-gray-600">
                                            Team SubPanel
                                          </div>

                                          {/* Team Lead (Always Required) */}
                                          <div className="flex flex-col items-center space-y-2">
                                            <div className="w-24 h-10 bg-gradient-to-br from-yellow-100 to-yellow-300 border border-yellow-400 rounded flex flex-col items-center justify-center">
                                              <Crown className="w-3 h-3 text-yellow-700" />
                                              <div className="text-xs font-bold text-yellow-700">
                                                Team Lead
                                              </div>
                                            </div>
                                            <div className="w-px h-4 bg-gray-400"></div>
                                          </div>

                                          {/* Hierarchical Team Structure */}
                                          <div className="space-y-3">
                                            {team.structure.map((member, i) => {
                                              const colorMap = {
                                                green:
                                                  'bg-gradient-to-br from-green-200 to-green-400 border-green-500 text-green-800',
                                                purple:
                                                  'bg-gradient-to-br from-purple-200 to-purple-400 border-purple-500 text-purple-800',
                                                blue: 'bg-gradient-to-br from-blue-200 to-blue-400 border-blue-500 text-blue-800',
                                                teal: 'bg-gradient-to-br from-teal-200 to-teal-400 border-teal-500 text-teal-800',
                                                orange:
                                                  'bg-gradient-to-br from-orange-200 to-orange-400 border-orange-500 text-orange-800',
                                              };

                                              return (
                                                <div
                                                  key={i}
                                                  className="flex flex-col items-center space-y-2"
                                                >
                                                  {/* Main Member */}
                                                  <div
                                                    className={`w-20 xs:w-24 sm:min-w-[100px] h-10 border rounded flex items-center justify-center px-2 ${colorMap[member.color]}`}
                                                  >
                                                    <div className="text-xs font-bold text-center leading-tight whitespace-nowrap">
                                                      {member.role}
                                                    </div>
                                                  </div>

                                                  {/* Subordinates if any */}
                                                  {member.subordinates &&
                                                    member.subordinates.length > 0 && (
                                                      <>
                                                        <div className="w-px h-4 bg-gray-400"></div>
                                                        <div className="flex items-center gap-3">
                                                          {member.subordinates.map((sub, subI) => (
                                                            <div
                                                              key={subI}
                                                              className="flex flex-col items-center space-y-2"
                                                            >
                                                              {/* Subordinate */}
                                                              <div
                                                                className={`w-18 xs:w-20 sm:min-w-[90px] h-10 border rounded flex items-center justify-center px-2 ${colorMap[sub.color]}`}
                                                              >
                                                                <div className="text-xs font-bold text-center leading-tight whitespace-nowrap">
                                                                  {sub.role}
                                                                </div>
                                                              </div>

                                                              {/* Sub-subordinates if any */}
                                                              {sub.subordinates &&
                                                                sub.subordinates.length > 0 && (
                                                                  <>
                                                                    <div className="w-px h-4 bg-gray-400"></div>
                                                                    <div className="flex items-center gap-2">
                                                                      {sub.subordinates.map(
                                                                        (subSub, subSubI) => (
                                                                          <div
                                                                            key={subSubI}
                                                                            className={`w-16 xs:w-18 sm:min-w-[80px] h-8 border rounded flex items-center justify-center px-1 ${colorMap[subSub.color]}`}
                                                                          >
                                                                            <div className="text-xs font-bold text-center leading-tight whitespace-nowrap">
                                                                              {subSub.role}
                                                                            </div>
                                                                          </div>
                                                                        ),
                                                                      )}
                                                                    </div>
                                                                  </>
                                                                )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      </>
                                                    )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Key insight callout */}
          <div className="mt-8 space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-blue-800 mb-1">
                    Fractal Governance Structure
                  </div>
                  <div className="text-sm text-blue-700">
                    Every level uses the same SubPanel leadership pattern: 1 Elect (override
                    authority) + 1-3 Directors (decision-makers). At community level: 1 Community
                    Elect + 1-3 Community Directors. At department level: 1 Department Elect + 1-3
                    Department Directors. This creates a consistent, scalable structure from
                    community-wide decisions down to specific departments.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-amber-800 mb-1">
                    Flexible Director Count & Transition Department Scaling
                  </div>
                  <div className="text-sm text-amber-700 space-y-2">
                    <p>
                      Each SubPanel requires only 1 Elect, but can have 1-3 Directors based on
                      workload and complexity. Start small and add Directors as the department
                      grows.
                    </p>
                    <p className="font-semibold text-amber-900">
                      Exception: Transition & Succession Department
                    </p>
                    <p>
                      <strong>Minimum: 8 people (2 complete transition SubPanels).</strong> The
                      Transition Department operates like a reserve team—elected observers who stay
                      informed but activate only during succession events.{' '}
                      <strong>Recommended scaling: 20% base capacity + 10% buffer.</strong>
                    </p>
                    <p>
                      <strong>Example with 10 departments:</strong> 10 departments × 4 people each =
                      40 active officials. 20% reserve = 8 people (2 transition SubPanels). Ideal
                      buffer: +4 people (1 additional SubPanel) = 12 total, providing 30% coverage
                      for high-turnover transition periods.
                    </p>
                    <p>
                      <strong>Why reserves matter:</strong> During initial elections and Nuclear
                      Option events, transition team members frequently get promoted to permanent
                      positions or resign. The buffer ensures continuity even when the reserve team
                      itself experiences turnover. These are elected positions with lower time
                      commitment (bi-weekly check-ins vs daily operations).
                    </p>
                    <p>
                      <strong>Flexible deployment:</strong> Transition teams deploy as complete
                      SubPanels, individual role replacements, or mixed teams based on which
                      positions were removed and what expertise is needed. Members serve as
                      "opportunity zone" for political careers—getting governance experience with
                      minimal time commitment, then potentially winning permanent elections after
                      serving as Acting officials.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-green-800 mb-1">
                    {cityName} Structure
                  </div>
                  <div className="text-sm text-green-700">
                    Each of these 10 departments for {cityName} becomes a dedicated team with its
                    own SubPanel leadership: 1 Department Elect + 1-3 Department Directors based on
                    workload. Within larger departments, teams can form to handle specific
                    functions, each with a Team Lead and support structure. For example, Group
                    Purchasing might have separate teams for Vendor Relations, Group Buy
                    Coordination, and Quality Assurance. This scalable structure ensures clear
                    chains of responsibility at every level, eliminating the confusion and delays
                    common in traditional governance systems.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                <div>
                  <div className="text-sm font-semibold text-indigo-800 mb-1">
                    Volunteers: The Lifeblood of Community Governance
                  </div>
                  <div className="text-sm text-indigo-700 space-y-2">
                    <p>
                      <strong>Every department runs on volunteer participation.</strong> For a city
                      of 80,000 people, professional staff can't handle everything alone. Each team
                      has 1 Team Lead with final authority, then a hierarchy of volunteer
                      coordinators and volunteers.
                    </p>
                    <p className="font-semibold">Typical 3-Layer Volunteer Structure:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>
                        <strong>Layer 1 (Manager/Director):</strong> Professional or elected lead
                        with accountability
                      </li>
                      <li>
                        <strong>Layer 2 (Volunteer Coordinators/Leads):</strong> Organize and direct
                        volunteer teams (teal boxes)
                      </li>
                      <li>
                        <strong>Layer 3 (Volunteers):</strong> Community members contributing time
                        and effort (orange boxes)
                      </li>
                    </ul>
                    <p>
                      <strong>Examples you'll see throughout:</strong> Emergency Management has
                      Response Leads coordinating 3-5 Emergency Volunteers each. Community Events
                      has Event Coordinators managing 3-5 Event Volunteers per festival. Parks &
                      Recreation has Volunteer Coordinators leading maintenance teams. Elections has
                      Poll Coordinators managing Poll Volunteers at voting locations.
                    </p>
                    <p className="font-semibold text-indigo-900">
                      Without volunteers, there is no community governance. The platform makes it
                      easy to see where you can contribute.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
}
